<script setup lang="ts" generic="T extends GenericForm">
import { ref } from "vue";
import type { MenuItem } from "primevue/menuitem";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "src/composables/useConfirm";
import type { GenericForm } from "src/types/form-type";
import SharedSearchInput from "../shared/SharedSearchInput.vue";

interface Props {
  searchQuery?: string;
  filters?: "simple" | "advanced";
  create?: boolean;
  delete?: boolean;
  deleteDisabled?: boolean;
  export?: boolean;
  clearFilters?: boolean;
  actions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  searchQuery: "",
  filters: "simple",
  create: true,
  delete: true,
  deleteDisabled: true,
  export: true,
  clearFilters: true,
  actions: true,
});

const emits = defineEmits<{
  "click:create": [val: void];
  "click:delete": [val: void];
  "click:export": [val: void];
  "click:clearFilters": [val: void];
  "click:resetTable": [val: void];
  "update:searchQuery": [val: string];
  "update:filters": [val: "simple" | "advanced"];
}>();

const { confirm } = useConfirm();
const toast = useToast();

const menu = ref();
const items = ref<MenuItem[]>([
  {
    label: "Export",
    icon: "pi pi-fw pi-upload",
    command: () => emits("click:export"),
  },
  {
    label: "Filters",
    icon: "pi pi-fw pi-filter",
    items: [
      {
        label: "Simple",
        command: () => emits("update:filters", "simple"),
      },
      {
        label: "Advanced",
        command: () => emits("update:filters", "advanced"),
      },
    ],
  },
  {
    label: "Clear Filters",
    icon: "pi pi-fw pi-filter-slash",
    command: () => emits("click:clearFilters"),
  },
  {
    label: "Reset Table",
    icon: "pi pi-fw pi-table",
    command: () => emits("click:resetTable"),
  },
]);

function toggleMenu(event: Event) {
  menu.value.toggle(event);
}

function onClickDelete(event: Event) {
  const { onAccept } = confirm(event);

  onAccept(() => {
    emits("click:delete");
  });
}
</script>
<template>
  <div class="flex flex-column md:flex-row md:justify-between md:align-items-center">
    <div class="flex justify-between space-x-2">
      <template v-if="actions">
        <Button
          type="button"
          label="Actions"
          size="small"
          outlined
          aria-haspopup="true"
          aria-controls="overlay_tmenu"
          @click="toggleMenu"
        />
        <TieredMenu id="overlay_tmenu" ref="menu" :model="items" popup />
      </template>

      <SharedSearchInput
        :model-value="searchQuery"
        placeholder="Search..."
        @update:model-value="emits('update:searchQuery', $event)"
        dense
      />


      <slot name="left" />
    </div>
    <div>
      <slot name="right" />
      <Button
        v-if="create"
        type="button"
        label="New"
        icon="pi pi-plus"
        size="small"
        class="mr-2"
        @click="emits('click:create')"
      />
      <Button
        v-if="props.delete"
        type="button"
        label="Delete"
        icon="pi pi-trash"
        color="warning"
        size="small"
        class="mr-2"
        :disabled="deleteDisabled"
        @click="onClickDelete"
      />
    </div>
  </div>
</template>
